import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Repeat, Calendar, CalendarDays, AlertTriangle, Users, GraduationCap } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import type { ScheduleBooking } from "./schedule/types";

type MoveChoice = "single" | "all_future" | null;

interface SubscriptionMoveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: ScheduleBooking;
  bookingType: "recurring" | "activity" | "training";
  activityDetails?: {
    title?: string;
    teamA?: string;
    teamB?: string;
    coachName?: string;
    participantCount?: number;
    hasResult?: boolean;
  };
  onChoice: (choice: MoveChoice) => void;
}

export default function SubscriptionMoveDialog({
  open,
  onOpenChange,
  booking,
  bookingType,
  activityDetails,
  onChoice,
}: SubscriptionMoveDialogProps) {
  const { t } = useTranslation("admin");
  const bStart = new Date(booking.start_time);
  const courtName = booking.courts?.name ?? "Bana";

  // Block moves for played matches
  if (bookingType === "activity" && activityDetails?.hasResult) {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {t("subscriptionMove.cannotMove")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("subscriptionMove.matchAlreadyPlayed")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("subscriptionMove.close")}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {bookingType === "recurring" && <Repeat className="h-5 w-5 text-blue-500" />}
            {bookingType === "activity" && <Users className="h-5 w-5 text-amber-500" />}
            {bookingType === "training" && <GraduationCap className="h-5 w-5 text-purple-500" />}
            {bookingType === "recurring" && t("subscriptionMove.belongsToSubscription")}
            {bookingType === "activity" && t("subscriptionMove.belongsToActivity")}
            {bookingType === "training" && t("subscriptionMove.belongsToTraining")}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <div className="rounded-lg border p-3 text-sm space-y-1">
                <p className="font-medium">{courtName} · {format(bStart, "EEEE d MMM HH:mm", { locale: sv })}</p>
                {bookingType === "activity" && activityDetails?.teamA && (
                  <p className="text-muted-foreground">
                    {activityDetails.teamA} vs {activityDetails.teamB}
                    {activityDetails.title && ` · ${activityDetails.title}`}
                  </p>
                )}
                {bookingType === "training" && activityDetails?.coachName && (
                  <p className="text-muted-foreground">
                    {t("subscriptionMove.coach")}: {activityDetails.coachName}
                    {activityDetails.participantCount != null && ` · ${activityDetails.participantCount} ${t("subscriptionMove.participants")}`}
                  </p>
                )}
              </div>

              {bookingType === "recurring" && (
                <p className="text-sm text-muted-foreground">
                  {t("subscriptionMove.moveQuestion")}
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2 px-1">
          {bookingType === "recurring" && (
            <>
              <Button
                variant="outline"
                className="w-full justify-start gap-2 h-auto py-3"
                onClick={() => onChoice("single")}
              >
                <Calendar className="h-4 w-4 shrink-0" />
                <div className="text-left">
                  <p className="font-medium text-sm">{t("subscriptionMove.moveThisWeekOnly")}</p>
                  <p className="text-xs text-muted-foreground">{t("subscriptionMove.createsException", { date: format(bStart, "d MMM", { locale: sv }) })}</p>
                </div>
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start gap-2 h-auto py-3"
                onClick={() => onChoice("all_future")}
              >
                <CalendarDays className="h-4 w-4 shrink-0" />
                <div className="text-left">
                  <p className="font-medium text-sm">{t("subscriptionMove.moveAllFuture")}</p>
                  <p className="text-xs text-muted-foreground">{t("subscriptionMove.changesSubscriptionFrom", { date: format(bStart, "d MMM", { locale: sv }) })}</p>
                </div>
              </Button>
            </>
          )}
          {(bookingType === "activity" || bookingType === "training") && (
            <Button
              variant="outline"
              className="w-full justify-start gap-2 h-auto py-3"
              onClick={() => onChoice("single")}
            >
              <Calendar className="h-4 w-4 shrink-0" />
              <div className="text-left">
                <p className="font-medium text-sm">{t("subscriptionMove.moveBooking")}</p>
                <p className="text-xs text-muted-foreground">
                  {bookingType === "activity"
                    ? t("subscriptionMove.allPlayersNotified")
                    : t("subscriptionMove.coachAndParticipantsNotified")}
                </p>
              </div>
            </Button>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>{t("subscriptionMove.cancel")}</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
